import { useState } from "react";
import { useSelector } from "react-redux";
import { Info, ArrowsLeftRight, UserMinus } from "@phosphor-icons/react";
import LoaderLoop from "../shared/loader/LoaderLoop";
import ModalCloseButton from "../ui/ModalCloseButton";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectAssignableTherapists,
} from "../../store/slices/authSlice";
import {
  useSetTherapistStatusMutation,
  useTransferPatientsMutation,
} from "../../store/services/therapistApi";
import { useToast } from "../../contexts/ToastContext";
import { memberName } from "./teamUtils";

/**
 * Deactivate a team member (never delete — medical-records compliance),
 * with an optional transfer of all patients + future appointments.
 * Ported from ClinicSettingsPage (CK-5004 semantics kept).
 */
function DeactivateMemberModal({ therapist, onClose, onDone }) {
  const clinicId = useSelector(selectClinicId);
  const actorId = useSelector(selectTherapistId);
  const actorName = useSelector(selectTherapistName);
  const assignable = useSelector(selectAssignableTherapists);
  const { showSuccess, showError } = useToast();
  const [setStatus] = useSetTherapistStatusMutation();
  const [transferPatients] = useTransferPatientsMutation();
  const [toId, setToId] = useState("");
  const [moveFuture, setMoveFuture] = useState(true);
  const [busy, setBusy] = useState(false);

  const targetId = therapist?.uid || therapist?.id;
  const targets = assignable.filter((t) => (t.uid || t.id) !== targetId);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      if (toId) {
        await transferPatients({
          clinicId,
          fromTherapistId: targetId,
          toTherapistId: toId,
          transferFutureAppointments: moveFuture,
          actorId,
          actorName,
        }).unwrap();
      }
      await setStatus({
        clinicId,
        therapistId: targetId,
        isActive: false,
        actorId,
        actorName,
        targetName: memberName(therapist),
      }).unwrap();
      showSuccess(toId ? "איש/אשת הצוות הושבת/ה והמטופלים הועברו" : "איש/אשת הצוות הושבת/ה");
      onDone?.();
      onClose();
    } catch (err) {
      console.error("[CK-5542] Error deactivating team member:", {
        errorCode: "CK-5542",
        errorMessage: err?.message,
        clinicId,
        targetTherapistId: targetId,
        toTherapistId: toId || null,
        timestamp: new Date().toISOString(),
      });
      showError(
        toId
          ? "שגיאה בהשבתה או בהעברת המטופלים. בדקו את רשימת המטופלים ונסו שוב."
          : "שגיאה בהשבתת איש/אשת הצוות. נסו שוב."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tm-overlay" onClick={onClose}>
      <div className="tm-modal narrow" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="tm-modal-header">
          <div className="tm-modal-title">
            <span className="tm-modal-title-icon">
              <UserMinus size={20} weight="fill" />
            </span>
            <div className="tm-modal-title-text">
              <span className="tm-eyebrow">צוות הקליניקה</span>
              <h3>השבתת {memberName(therapist)}</h3>
            </div>
          </div>
          <div className="tm-modal-header-actions">
            <ModalCloseButton onClick={onClose} />
          </div>
        </div>

        <div className="tm-body">
          <div className="tm-note">
            <Info size={16} weight="fill" />
            <span>
              לא ניתן למחוק איש/אשת צוות מהמערכת. החשבון יהפוך ל"לא פעיל" וכל המידע המשויך (מטופלים, תיעוד פגישות,
              סיכומים) יישאר שמור. ניתן להפעיל מחדש בכל עת.
            </span>
          </div>

          {targets.length > 0 && (
            <>
              <div className="tm-field">
                <label className="tm-label">
                  <ArrowsLeftRight size={14} weight="bold" style={{ verticalAlign: "middle" }} /> העברת מטופלים
                  למטפל/ת אחר/ת (אופציונלי)
                </label>
                <select className="tm-select" value={toId} onChange={(e) => setToId(e.target.value)}>
                  <option value="">ללא העברה</option>
                  {targets.map((t) => (
                    <option key={t.uid || t.id} value={t.uid || t.id}>
                      {memberName(t)}
                    </option>
                  ))}
                </select>
              </div>
              {toId && (
                <label className="tm-check">
                  <input type="checkbox" checked={moveFuture} onChange={(e) => setMoveFuture(e.target.checked)} />
                  <span>העבר/י גם תורים עתידיים</span>
                </label>
              )}
            </>
          )}
        </div>

        <div className="tm-footer">
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
            ביטול
          </button>
          <button type="button" className="tm-btn danger" onClick={handleConfirm} disabled={busy}>
            {busy ? <LoaderLoop size="xs" /> : null}
            {busy ? "משבית..." : "השבתה"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeactivateMemberModal;
